import { useCallback, useEffect, useState } from 'react';
import { printingApi } from '@verlo/shared/services/api';
import { Button } from '@verlo/shared/components/ui/button';
import { Signal } from '@verlo/shared/components/ui/signal';
import { toast } from 'sonner';
import { FileText, RotateCw } from 'lucide-react';

/**
 * The venue's printers, as this till sees them (#94).
 *
 * It lives here rather than in the back office because the till is the machine that dials
 * them: the spool is node-owned and replicates nowhere, so the office can say which
 * printers exist and only the till can say whether one is working. Test a printer here,
 * and send a stuck ticket again here.
 */
const STATE = {
  ok: { tone: 'ok', label: 'printing' },
  busy: { tone: 'info', label: 'sending' },
  retrying: { tone: 'warn', label: 'retrying' },
  down: { tone: 'stop', label: 'not printing' },
  idle: { tone: null, label: 'nothing sent yet' },
};

const PrintersPanel = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const read = useCallback(() => {
    printingApi.status().then(({ data: d }) => setData(d)).catch(() => setData({ printers: [] }));
  }, []);
  useEffect(() => {
    read();
    const t = setInterval(read, 20000);
    return () => clearInterval(t);
  }, [read]);

  if (!data) return null;
  const printers = data.printers || [];
  // The stuck jobs themselves, queried by status rather than sifted out of `recent` —
  // which is the last twenty jobs of any status, so a busy bar pushed the kitchen's
  // dead tickets out of this list within minutes (#116).
  const stuck = data.needs_attention || [];

  const test = async (p) => {
    setBusy(p.id);
    try {
      await printingApi.test(p.id);
      toast.success(`Test page sent to ${p.name}`);
      setTimeout(read, 2500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send a test page');
    } finally { setBusy(null); }
  };

  const again = async (job) => {
    setBusy(job.id);
    try {
      await printingApi.retry(job.id);
      toast.success('Sent again');
      setTimeout(read, 2500);
    } catch {
      toast.error('Could not send it again');
    } finally { setBusy(null); }
  };

  const clear = async (job) => {
    setBusy(job.id);
    try {
      await printingApi.dismiss(job.id);
      toast.success('Cleared');
      setTimeout(read, 1000);
    } catch {
      toast.error('Could not clear it');
    } finally { setBusy(null); }
  };

  return (
    <div className="rounded-xl border bg-white p-4" data-testid="till-printers">
      <p className="mb-2 text-[11px] uppercase tracking-wide text-ink-faint">Printers</p>
      {printers.length === 0 ? (
        <p className="text-sm text-ink-faint">
          No printer is set up for this site. Tickets still appear on the kitchen screen.
        </p>
      ) : (
        <div className="space-y-1">
          {printers.map((p) => {
            const state = STATE[p.state] || STATE.idle;
            return (
              <div key={p.id} className="flex items-center gap-2 py-1.5 text-sm border-b last:border-0" data-testid={`till-printer-${p.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-ink">{p.name}</p>
                  <p className="font-mono text-[11px] tabular-nums text-ink-faint">{p.host}:{p.port}</p>
                </div>
                {p.queued > 0 && <span className="text-[12px] text-ink-faint tabular-nums">{p.queued} waiting</span>}
                {state.tone
                  ? <Signal tone={state.tone}>{state.label}</Signal>
                  : <span className="text-[12px] text-ink-faint">{state.label}</span>}
                <Button size="sm" variant="outline" className="h-8 shrink-0" disabled={busy === p.id}
                        onClick={() => test(p)} data-testid={`till-printer-test-${p.id}`}>
                  <FileText size={13} className="mr-1.5" />Test
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {stuck.length > 0 && (
        <div className="mt-3 border-t pt-3">
          <p className="mb-1.5 text-[11px] uppercase tracking-wide text-signal-stop">Not delivered</p>
          <div className="space-y-1">
            {stuck.slice(0, 6).map((j) => (
              <div key={j.id} className="flex items-center gap-2 text-xs" data-testid={`till-stuck-${j.id}`}>
                <span className="min-w-0 flex-1 truncate text-ink-soft">
                  {j.kind} · {j.reference || j.order_id} · {j.printer_name}
                  {j.error ? <span className="text-ink-faint"> · {j.error}</span> : null}
                </span>
                <Button size="sm" variant="outline" className="h-7 shrink-0" disabled={busy === j.id}
                        onClick={() => again(j)} data-testid={`till-retry-${j.id}`}>
                  <RotateCw size={12} className="mr-1.5" />Send again
                </Button>
                <Button size="sm" variant="ghost" className="h-7 shrink-0 text-ink-faint" disabled={busy === j.id}
                        onClick={() => clear(j)} data-testid={`till-dismiss-${j.id}`}
                        title="It was dealt with another way. Stop it asking.">
                  Clear
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PrintersPanel;
